// pageParam — the ONE deep-link parameter reader every app page uses.
//
// "Open always opens the exact spot" (D2 §9) needs three things to hold, and
// the third is the one that kept failing: the target app must read its
// deep-link param WHEN IT MOUNTS, not only when it is already warm. A room's
// "Open" on a Document or a Task set the URL first and mounted the app
// second, so every app that read its param once — at a moment that had
// already passed — landed on its generic list instead of the object.
//
// The reason five apps each grew their own reader is that a param can arrive
// through TWO different channels, and neither one alone is enough:
//
//   1. `window.location.search` — a cold load of `/mail?thread=…`, a module's
//      `setModuleRouteQuery` write, and the PageSlot's own URL mirror
//      (canvas/components/PageSlot.js `_slotUrlNext` copies the PRIMARY
//      pane's route query into the address bar).
//   2. the PANE'S OWN ROUTE — `root._canvasSlot` / `root._canvasSlotSecond`
//      carry the full href (`/mail?thread=…`), and PageSlot stamps that href
//      on the keep-alive host it mounts the page into (`host._slotPath`,
//      beside `host._slotPaneKey`). The SECOND pane is mirrored nowhere else:
//      the address bar belongs to the primary, so a split-pane open is
//      readable ONLY here.
//
// So the reader walks up from the calling element to the nearest slot host
// and merges: the pane's route wins per key, the address bar fills the rest.
// That fallback is what makes a cold `/?page=/vault-training&node=<key>` work
// — the shell re-opens the slot with the PATH, while `node=` stays in
// `location.search` (the module's own `setModuleRouteQuery` writes there too).
// The one place the fallback is refused is a SECOND pane: it must never
// inherit the primary's thread/ticket/event id — and that holds whether or
// not it carries a query of its own, because the address bar it would fall
// back to IS the primary's mirror.
//
// Two faces, ONE implementation (Rule 63):
//   • `pageParam(el, key)` / `pageParams(el)` — pure, for shell code + tests.
//   • `el.call('readPageParam', key)` / `el.call('readPageParams')` — the
//     registry face every app package and every workspace MODULE uses
//     (module bridge: workspaceProjects.js, beside setModuleRouteQuery).
//     App packages must not import across packages for this; el.call is the
//     canonical channel and it works pre-fold on a lazily-merged package.

const _SECOND_PANE = '_canvasSlotSecond'

// The query half of a slot route href, or null when it carries none.
const _queryOf = (route) => {
  const raw = String(route || '')
  const q = raw.indexOf('?')
  if (q === -1) return null
  return new URLSearchParams(raw.slice(q + 1).split('#')[0])
}

// Nearest ancestor PageSlot keep-alive host — the element PageSlot mounts a
// page def into, stamped with the FULL route (`_slotPath`) and its pane key.
// Returns null when the element is not inside a slot at all (the real-route
// case), so a stale `_canvasSlot` can never leak into a page that is the live
// route. Hop-capped like slotPaneKeyFor, so a malformed parent chain can
// never hang a mount.
const _slotHostOf = (el) => {
  let node = el
  let hops = 0
  while (node && hops < 100) {
    if (typeof node._slotPath === 'string' && node._slotPath) return node
    if (node.parent === node) break
    node = node.parent
    hops++
  }
  return null
}

// The same walk, exported for the one other reader that needs the HOST and
// not a param: `setModuleRouteQuery` (moduleOpenBridge.js) rewrites the pane
// route a record param rides on, and must stamp the host it lives in before
// it does, or PageSlot reads the new route as a deep-link change and
// re-mounts the page.
export const slotHostOf = (el) => _slotHostOf(el)

// Every deep-link param visible to THIS element, pane route winning per key.
export const pageParams = (el) => {
  const host = _slotHostOf(el)
  const paneQuery = host ? _queryOf(host._slotPath) : null
  // A SECOND pane is self-contained — see header. Not "a second pane that
  // happens to carry a query": the address bar mirrors the PRIMARY pane's
  // route (PageSlot `_writeSlotUrl` / `_slotUrlNext`), so the location
  // fallback is the primary's params by construction, and a second pane with
  // NO query of its own inherited them. Measured 2026-09-10 on the local
  // shell against staging: primary `/notes?id=PRIMARY-AAA`, second pane
  // `/tasks`, and `readPageParam('id')` inside the SECOND pane answered
  // `PRIMARY-AAA` — the address bar read `?page=%2Fnotes%3Fid%3DPRIMARY-AAA&
  // id=PRIMARY-AAA`. Same-app case is the damaging one (primary `/notes?id=A`
  // + second `/notes` opened document A twice, side by side). A second pane's
  // params are its OWN route's query, or none at all.
  if (host && host._slotPaneKey === _SECOND_PANE) return paneQuery || new URLSearchParams()
  const search =
    typeof window !== 'undefined' && window.location ? window.location.search || '' : ''
  const out = new URLSearchParams(search)
  if (paneQuery) for (const [k, v] of paneQuery.entries()) out.set(k, v)
  return out
}

// One param, or null. Empty string reads as absent — `?thread=` is not a
// thread, and every caller here treats "" as "nothing to open".
export const pageParam = (el, key) => {
  const k = String(key || '')
  if (!k) return null
  const v = pageParams(el).get(k)
  return v != null && v !== '' ? v : null
}

// The SURFACE this element is on, as a clean path (no query, no hash): the
// pane's own route when the element sits in a PageSlot pane, else the live
// pathname. Every app page grew an `_onRoute` predicate that computes this by
// hand from `root._canvasSlot` — which names the PRIMARY pane even when the
// caller lives in the second one. The workspace prefix is NOT stripped here
// (this module stays import-free); callers that need it already carry
// `stripWorkspacePrefix`.
export const pageRoute = (el) => {
  const host = _slotHostOf(el)
  if (host) return String(host._slotPath).split('?')[0].split('#')[0]
  if (typeof window === 'undefined' || !window.location) return ''
  return window.location.pathname || ''
}

// The hash belongs to the caller's surface. A pane must not inherit a
// room/note hash from the underlying page or from its sibling pane.
// Root callers (for example assistantThreadUrl) still read the address bar.
export const pageHash = (el) => {
  const host = _slotHostOf(el)
  if (host) {
    const route = String(host._slotPath)
    const index = route.indexOf('#')
    return index < 0 ? '' : route.slice(index)
  }
  return typeof window !== 'undefined' && window.location ? window.location.hash || '' : ''
}

// Registry faces — `this` is the calling element (el.call binds it).
export function readPageParam(key) {
  return pageParam(this, key)
}

export function readPageHash() {
  return pageHash(this)
}

export function readPageParams() {
  return pageParams(this)
}

export function readPageRoute() {
  return pageRoute(this)
}

// The pane's own ABSOLUTE url — origin + route + query + hash, for a share
// link (Validators / node "Copy link", WS-SHELL-MODULE-BRIDGE-NO-CURRENT-
// URL-1). The origin is THIS tab's actual host (`window.location`), never
// `workspaceUrl`'s canonical channel-shell origin: `workspaceUrl` normalizes
// an unrecognised custom domain down to `https://my.symbols.app`, the wrong
// host for a link copied FROM that domain. The path/query/hash still go
// through pageRoute/pageParams (pane-aware), so a SECOND pane answers its
// own address, never the primary's.
export const pageUrl = (el) => {
  if (typeof window === 'undefined' || !window.location) return ''
  const origin = `${window.location.protocol}//${window.location.host}`
  const query = pageParams(el).toString()
  return `${origin}${pageRoute(el)}${query ? `?${query}` : ''}${pageHash()}`
}

export function readPageUrl() {
  return pageUrl(this)
}
